/**
 * Client enhancer for Liquid-rendered product-card colour swatches.
 *
 * The Liquid card (snippets/curaprox-card-swatches.liquid) can only render the
 * first few colour swatches per card: each swatch costs a dynamic
 * `shop.metaobjects['visual'][handle]` lookup, and Shopify hard-caps those at
 * 20 UNIQUE handles PER PAGE — a featured-collection grid of toothbrushes (CS
 * 12460 alone has 36 colours) blows the cap and blanks later cards
 * (client 2026-08-18).
 *
 * This enhancer removes that ceiling on the permanent Liquid surfaces (homepage
 * featured collection). When a `.cpc-swatches` row carries a `data-cpc-colors`
 * payload (emitted only when there are MORE colours than the card rendered), it
 * rebuilds the row from that payload — every colour, correct tint — using
 * window.CuraproxVisualSwatchMap (built uncapped via paginate in
 * snippets/curaprox-visual-swatch-map.liquid). No metaobject lookups happen
 * client-side, so there is no cap.
 *
 * The output markup is byte-compatible with the Liquid card and the two Meili
 * renderers so the shared click handler (assets/card-swatch-add.js) drives it
 * unchanged. Keep the priority tree (image > split-disc > head svg > toothbrush
 * svg > disc) in sync with the `swatchVisual` functions in meilisearch-core.js +
 * meilisearch-collection.js and the Liquid card.
 *
 * Progressive enhancement: with no JS, or no payload, the Liquid card's first
 * few swatches + "+N" pill stay exactly as rendered.
 */
(() => {
  'use strict';

  const MAP = window.CuraproxVisualSwatchMap || {};

  // Toothbrush + head SVG templates are emitted once per page by the swatch-map
  // snippet's siblings (the Meili config also carries them). Fall back to the
  // ones the page already exposes; if none, toothbrush swatches degrade to a disc.
  function tpl(name) {
    const el = document.getElementById(name);
    return el ? el.textContent : '';
  }
  const TOOTHBRUSH_SVG = window.CuraproxToothbrushSvg || tpl('cpc-toothbrush-svg-template') || '';
  const HEAD_SVG = window.CuraproxHeadSvg || tpl('cpc-head-svg-template') || '';

  function esc(s) {
    if (s == null) return '';
    const d = document.createElement('div');
    d.textContent = String(s);
    return d.innerHTML;
  }

  // Band colour for one part of a multi-colour value ("Dark Blue" out of
  // "White/Dark Blue/Pink"): exact single-colour entry first, else the first
  // entry whose handle starts with the part, else neutral grey. Mirrors the
  // partColor() in the Meili renderers.
  function handleize(s) {
    return String(s == null ? '' : s)
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function partColor(part) {
    const ph = handleize(String(part).trim());
    if (ph && MAP[ph] && MAP[ph].body) return MAP[ph].body;
    const prefix = ph + '-';
    for (const k in MAP) {
      if (k.indexOf(prefix) === 0 && MAP[k].body) return MAP[k].body;
    }
    return '#d9d9d9';
  }

  // Visual for one swatch — the PDP priority tree. `flags` = {isTB,isHead,isRefill}.
  function swatchVisual(color, idx, flags, idSuffix) {
    const vis = MAP[color.handle] || {};
    let vars = '';
    if (vis.img) {
      return { vars: '--toothbrush-image:url(' + vis.img + ');', html: '<span class="cpc-swatch__img"></span>' };
    }
    const parts = String(color.name || '').split('/');
    if (parts.length >= 3 || (parts.length === 2 && flags.isHead)) {
      const n = Math.min(parts.length, 3);
      for (let i = 0; i < n; i++) vars += '--trio-' + (i + 1) + ':' + partColor(parts[i]) + ';';
      const duo = parts.length === 2 ? ' cpc-swatch__trio--duo' : '';
      return { vars, html: '<span class="cpc-swatch__trio' + duo + '"></span>' };
    }
    if (flags.isRefill) {
      if (vis.body) vars += '--body:' + vis.body + ';';
      return { vars, html: HEAD_SVG || '<span class="cpc-swatch__disc"></span>' };
    }
    if (flags.isTB) {
      if (vis.body) vars += '--body:' + vis.body + ';';
      if (vis.bristles) vars += '--bristles:' + vis.bristles + ';';
      const svg = TOOTHBRUSH_SVG
        ? TOOTHBRUSH_SVG.split('__UID__').join(color.handle + '-' + idSuffix + '-' + idx)
        : '<span class="cpc-swatch__disc"></span>';
      return { vars, html: svg };
    }
    vars = '--disc-color:' + (vis.body || '#d9d9d9') + ';';
    return { vars, html: '<span class="cpc-swatch__disc"></span>' };
  }

  function buildSwatchSpan(color, idx, flags, idSuffix, title, showText) {
    const sv = swatchVisual(color, idx, flags, idSuffix);
    const label = showText + ': ' + title + ' — ' + color.name;
    // Identical markup contract to the Liquid card + Meili renderers so
    // card-swatch-add.js drives it unchanged.
    return (
      '<span class="cpc-swatch" style="' + esc(sv.vars) + '"' +
      ' data-cpc-swatch-add' +
      ' data-variant-id="' + esc(color.variantId) + '"' +
      ' data-media-id="' + esc(color.mediaId) + '"' +
      ' data-image="' + esc(color.image) + '"' +
      ' data-hover-image="' + esc(color.hover) + '"' +
      ' data-variant-url="' + esc(color.url) + '"' +
      ' role="button" tabindex="0" aria-pressed="false"' +
      ' aria-label="' + esc(label) + '"' +
      (color.blocked ? ' aria-disabled="true"' : '') +
      '>' + sv.html + '</span>'
    );
  }

  function enhanceRow(row) {
    if (row.dataset.cpcEnhanced) return;
    let colors;
    try { colors = JSON.parse(row.dataset.cpcColors); } catch (e) { return; }
    if (!Array.isArray(colors) || !colors.length) return;

    const flags = {
      isTB: row.dataset.cpcIsTb === 'true',
      isHead: row.dataset.cpcIsHead === 'true',
      isRefill: row.dataset.cpcIsRefill === 'true'
    };
    const title = row.dataset.cpcProductTitle || '';
    // The overlay row uses a different id suffix than the inline row so the
    // inline SVG mask ids stay unique between the two copies on one card.
    const idSuffix = row.classList.contains('cpc-swatches--inline') ? 'd' : 'm';
    const showText = (window.CuraproxSwatchShowText) || 'Show';
    const max = Math.max(1, parseInt(row.dataset.cpcMax, 10) || 3);

    // Match the card design (and the Meili renderers): show the first `max`
    // swatches, then a "+N" pill for the rest — NOT every colour inline. The
    // enhancer's job is to render those first `max` with correct tints even on
    // cards the Liquid render starved (cap exhaustion), and to make the "+N"
    // count exact. The pill keeps the same `data-cpc-swatch-more` contract so
    // card-swatch-add.js opens the quick-add chooser.
    const shownColors = colors.slice(0, max);
    let html = shownColors
      .map((c, i) => buildSwatchSpan(c, i, flags, idSuffix, title, showText))
      .join('');

    const extra = colors.length - shownColors.length;
    if (extra > 0) {
      const productUrl = (colors[0] && colors[0].url ? String(colors[0].url).split('?')[0] : '#');
      html += '<a class="cpc-swatch-more functional" data-cpc-swatch-more' +
        ' href="' + esc(productUrl) + '"' +
        ' aria-label="' + esc('+' + extra + ' ' + title) + '">+' + extra + '</a>';
    }

    row.innerHTML = html;
    row.dataset.cpcEnhanced = '1';
  }

  function run(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cpc-swatches[data-cpc-colors]').forEach(enhanceRow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document));
  } else {
    run(document);
  }

  // Re-run for cards injected after load (Section Rendering API, carousels).
  document.addEventListener('shopify:section:load', (e) => run(e.target));
})();
